import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Button } from "@/shared/components/ui/button";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellInput,
  ETableHead,
  ETableHeader,
  ETableOptions,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  updateMasterDataPdfCache,
  usePatchMasterDataPdfMutation,
  useShowMasterDataPdfQuery,
} from "@/services/backend/company/api/master-data-pdf";
import {
  addInfoblockGroup,
  infoblockLineCount,
  MasterDataPdfDefault,
  MasterDataPdfInfoblockData,
  MasterDataPdfInfoblockLine,
  maxInfoblockLines,
  moveInfoblockGroup,
  removeInfoblockGroup,
  setInfoblockLines,
} from "@/services/backend/company/types/master-data-pdf";

export function MasterDataPdfInfoblockDataCard({ id }: { id: string }) {
  const { data, error, isLoading } = useShowMasterDataPdfQuery({ id });

  return (
    <div>
      {data && <Content data={data} />}
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <CardSkeleton />}
    </div>
  );
}

function Content({ data }: { data: MasterDataPdfDefault }) {
  const [set, { error }] = useOptimisticDebouncedMutation(
    { id: data.id },
    updateMasterDataPdfCache,
    usePatchMasterDataPdfMutation(),
  );

  const setInfoblockData = (infoblockData: MasterDataPdfInfoblockData) =>
    set({ id: data.id, infoblockData }, { ...data, infoblockData });

  const lineCount = infoblockLineCount(data.infoblockData);
  // The budget is a property of the whole block, so the backend
  // reports it under the field rather than on a single line.
  const blockErrors = inputValidationErrorMessages(error, "infoblockData");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Infoblock")}</CardTitle>
        <CardDescription>
          {t(
            "Der Block oben rechts. Gruppen werden durch einen kleinen Abstand getrennt. Ist ein Schlüssel gesetzt, steht er links und der Wert rechts.",
          )}
        </CardDescription>
        <CardAction>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setInfoblockData(addInfoblockGroup(data.infoblockData))
            }
          >
            <Plus className="h-4 w-4" />
            {t("Gruppe")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p
          className={
            lineCount > maxInfoblockLines
              ? "text-destructive text-sm"
              : "text-muted-foreground text-sm"
          }
        >
          {`${lineCount} / ${maxInfoblockLines} ${t("Zeilen")}`}
        </p>
        {blockErrors.length > 0 && (
          <ul className="text-destructive text-sm">
            {blockErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
        {data.infoblockData.groups.map((group, groupIndex) => (
          <GroupTable
            // The groups have no id; their position is their identity.
            key={groupIndex}
            lines={group.lines}
            groupIndex={groupIndex}
            groupCount={data.infoblockData.groups.length}
            onLinesChange={(lines) =>
              setInfoblockData(
                setInfoblockLines(data.infoblockData, groupIndex, lines),
              )
            }
            onMove={(by) =>
              setInfoblockData(
                moveInfoblockGroup(data.infoblockData, groupIndex, by),
              )
            }
            onRemove={() =>
              setInfoblockData(
                removeInfoblockGroup(data.infoblockData, groupIndex),
              )
            }
          />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * LineRow adapts a line to what the table needs.
 *
 * The lines are stored without an identity of their own, but both
 * the sortable context and {@link ETableOptions} address rows by id
 * and keep an order field, so the lines are wrapped for the length
 * of a render. The position is the identity, which is why the id is
 * derived from the index: every change maps straight back to lines,
 * so the ids are rebuilt along with the rows.
 */
interface LineRow extends MasterDataPdfInfoblockLine {
  id: string;
  order: number;
}

function toRows(lines: MasterDataPdfInfoblockLine[]): LineRow[] {
  return lines.map((line, index) => ({
    ...line,
    id: String(index),
    order: index,
  }));
}

function toLines(rows: LineRow[]): MasterDataPdfInfoblockLine[] {
  return rows.map(({ key, value, bold }) => ({ key, value, bold }));
}

function newLineRow(): LineRow {
  return { id: "new", order: 0, key: "", value: "", bold: false };
}

function GroupTable({
  lines,
  groupIndex,
  groupCount,
  onLinesChange,
  onMove,
  onRemove,
  disabled = false,
}: {
  lines: MasterDataPdfInfoblockLine[];
  groupIndex: number;
  groupCount: number;
  onLinesChange: (lines: MasterDataPdfInfoblockLine[]) => void;
  onMove: (by: number) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const rows = toRows(lines);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    if (disabled) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rows.findIndex((row) => row.id === active.id);
    const newIndex = rows.findIndex((row) => row.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onLinesChange(toLines(arrayMove(rows, oldIndex, newIndex)));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={rows.map((row) => row.id)}
        strategy={verticalListSortingStrategy}
      >
        <ETable>
          <ETableHeader>
            <ETableRow>
              <ETableHead colSpan={5}>
                <div className="flex items-center justify-between">
                  <span>{`${t("Gruppe")} ${groupIndex + 1}`}</span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled || groupIndex === 0}
                      aria-label={t("Gruppe nach oben")}
                      onClick={() => onMove(-1)}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled || groupIndex === groupCount - 1}
                      aria-label={t("Gruppe nach unten")}
                      onClick={() => onMove(1)}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      aria-label={t("Gruppe entfernen")}
                      onClick={onRemove}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </ETableHead>
            </ETableRow>
          </ETableHeader>
          <ETableBody className="[&>tr:last-child>td]:border-b">
            {rows.length === 0 && (
              <ETableRow>
                <ETableCell colSpan={5} className="!py-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <span>
                      {t("Es sind noch keine Zeilen hinzugefügt worden.")}
                    </span>
                    <Button
                      onClick={() =>
                        onLinesChange(toLines([...rows, newLineRow()]))
                      }
                      variant="secondary"
                      size="sm"
                      disabled={disabled}
                    >
                      <Plus />
                    </Button>
                  </div>
                </ETableCell>
              </ETableRow>
            )}
            {rows.map((row, index) => (
              <Row
                key={row.id}
                row={row}
                rows={rows}
                index={index}
                onRowsChange={(next) => onLinesChange(toLines(next))}
                disabled={disabled}
              />
            ))}
          </ETableBody>
        </ETable>
      </SortableContext>
    </DndContext>
  );
}

function Row({
  row,
  rows,
  index,
  onRowsChange,
  disabled,
}: {
  row: LineRow;
  rows: LineRow[];
  index: number;
  onRowsChange: (rows: LineRow[]) => void;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const update = (updated: Partial<MasterDataPdfInfoblockLine>) =>
    onRowsChange(
      rows.map((existing) =>
        existing.id === row.id ? { ...existing, ...updated } : existing,
      ),
    );

  return (
    <ETableRow ref={setNodeRef} style={style}>
      <ETableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="hover:bg-muted cursor-grab rounded p-1 disabled:cursor-default disabled:opacity-50"
          disabled={disabled}
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </ETableCell>
      <ETableCellInput
        className="max-w-40"
        placeholder={t("Schlüssel")}
        value={row.key}
        disabled={disabled}
        onChange={(e) => update({ key: e.target.value })}
      />
      <ETableCellInput
        placeholder={t("Wert")}
        value={row.value}
        disabled={disabled}
        onChange={(e) => update({ value: e.target.value })}
      />
      <ETableCellCheckbox
        label={t("Fett")}
        checked={row.bold}
        disabled={disabled}
        onCheckedChange={(bold) => update({ bold: bold === true })}
      />
      <ETableCell className="w-10">
        <ETableOptions
          item={row}
          rowIndex={index}
          items={rows}
          onItemsChange={onRowsChange}
          newItem={newLineRow}
          disabled={disabled}
        />
      </ETableCell>
    </ETableRow>
  );
}
